import React, { useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { HistogramConfig } from '../../types';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface HistogramChartProps {
  config: HistogramConfig;
  data?: number[];
  title?: string;
  className?: string;
}

/**
 * Histogram chart component for displaying simulation results
 */
export const HistogramChart: React.FC<HistogramChartProps> = ({
  config,
  data = [],
  title = 'Trial Outcome Distribution',
  className = ''
}) => {
  const chartRef = useRef<ChartJS<'bar'>>(null);

  // Generate bin labels
  const generateBinLabels = (numBins: number, binSize: number): string[] => {
    return Array.from({ length: numBins }, (_, i) => {
      const binMin = i * binSize;
      const binMax = (i + 1) * binSize;
      return `$${(binMin / 1000).toFixed(0)}k - $${(binMax / 1000).toFixed(0)}k`;
    });
  };

  // Process data into histogram bins
  const processDataIntoBins = (values: number[], config: HistogramConfig): number[] => {
    const binCounts = Array(config.numBins).fill(0);
    
    values.forEach(value => {
      const binIndex = Math.max(0, Math.min(config.numBins - 1, Math.floor(value / config.binSize)));
      binCounts[binIndex]++;
    });
    
    return binCounts;
  };

  const binLabels = generateBinLabels(config.numBins, config.binSize);
  const binCounts = processDataIntoBins(data, config);

  const chartData = {
    labels: binLabels,
    datasets: [
      {
        label: title,
        data: binCounts,
        backgroundColor: 'rgba(54, 162, 235, 0.6)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1,
      },
    ],
  };

  const options: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: !!title,
        text: title,
      },
      tooltip: {
        callbacks: {
          title: (tooltipItems) => {
            return tooltipItems[0].label;
          },
          label: (tooltipItem) => {
            return `Trials: ${tooltipItem.raw}`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Number of Trials',
        },
      },
      x: {
        title: {
          display: true,
          text: 'Total Final Balance',
        },
      },
    },
    animation: {
      duration: 0, // Disable animation for performance
    },
  };

  return (
    <div className={`histogram-chart-container ${className}`}>
      <Bar ref={chartRef} data={chartData} options={options} />
    </div>
  );
};

/**
 * Results histogram with color coding for positive/negative outcomes
 */
interface ResultsHistogramProps {
  results: Array<{ finalBalance: number; wasOverdrawn: boolean }>;
  className?: string;
}

export const ResultsHistogram: React.FC<ResultsHistogramProps> = ({
  results,
  className = ''
}) => {
  const chartRef = useRef<ChartJS<'bar'>>(null);

  if (!results || results.length === 0) {
    return (
      <div className={`results-histogram-container ${className}`}>
        <div className="no-data">No results to display</div>
      </div>
    );
  }

  // Calculate nice axis limits
  const finalBalances = results.map(r => r.finalBalance);
  const rawMin = Math.min(...finalBalances);
  const rawMax = Math.max(...finalBalances);
  
  // Determine nice bin size and range
  const range = rawMax - rawMin;
  const targetBins = 25;
  let roughBinSize = range / targetBins;
  
  const magnitude = Math.pow(10, Math.floor(Math.log10(roughBinSize)));
  const residual = roughBinSize / magnitude;
  
  let niceBinSize: number;
  if (residual > 5) {
    niceBinSize = 10 * magnitude;
  } else if (residual > 2.5) {
    niceBinSize = 5 * magnitude;
  } else if (residual > 1.5) {
    niceBinSize = 2.5 * magnitude;
  } else {
    niceBinSize = 1 * magnitude;
  }
  niceBinSize = Math.max(1, niceBinSize);

  let niceMin = Math.floor(rawMin / niceBinSize) * niceBinSize;
  let niceMax = Math.ceil(rawMax / niceBinSize) * niceBinSize;

  // Special handling for predominantly positive data
  if (rawMin >= 0 && niceMin > 0) {
    if (rawMin < niceBinSize || rawMin < rawMax * 0.1) {
      niceMin = 0;
    }
  }

  if (niceMin === niceMax) {
    niceMax += niceBinSize;
  }

  const numBins = Math.round((niceMax - niceMin) / niceBinSize);

  // Generate labels and colors
  const binLabels = [];
  const binCounts = Array(numBins).fill(0);
  const backgroundColors = [];
  const defaultColor = 'rgba(75, 192, 192, 0.6)';
  const errorColor = 'rgba(255, 99, 132, 0.6)';

  for (let i = 0; i < numBins; i++) {
    const binMin = niceMin + i * niceBinSize;
    const binMax = binMin + niceBinSize;
    
    const formatLabel = (val: number) => {
      if (Math.abs(val) >= 1000000) return `$${(val / 1000000).toFixed(1)}M`;
      if (Math.abs(val) >= 1000) return `$${(val / 1000).toFixed(0)}k`;
      return `$${val.toFixed(0)}`;
    };
    
    binLabels.push(`${formatLabel(binMin)} to ${formatLabel(binMax)}`);
    backgroundColors.push(binMax <= 0 ? errorColor : defaultColor);
  }

  // Populate bin counts
  finalBalances.forEach(balance => {
    let binIndex = Math.floor((balance - niceMin) / niceBinSize);
    binIndex = Math.max(0, Math.min(numBins - 1, binIndex));
    if (balance === niceMax) {
      binIndex = numBins - 1;
    }
    binCounts[binIndex]++;
  });

  const chartData = {
    labels: binLabels,
    datasets: [
      {
        label: 'Final Balance Distribution',
        data: binCounts,
        backgroundColor: backgroundColors,
        borderColor: backgroundColors.map(color => color.replace('0.6', '1')),
        borderWidth: 1,
      },
    ],
  };

  const options: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: 'Final Balance Distribution',
      },
      tooltip: {
        callbacks: {
          title: (tooltipItems) => {
            return tooltipItems[0].label;
          },
          label: (tooltipItem) => {
            return `Trials: ${tooltipItem.raw}`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Number of Trials',
        },
      },
      x: {
        title: {
          display: true,
          text: 'Total Final Balance',
        },
      },
    },
    animation: {
      duration: 0,
    },
  };

  return (
    <div className={`results-histogram-container ${className}`}>
      <Bar ref={chartRef} data={chartData} options={options} />
    </div>
  );
};
